import { useEffect, useCallback, useRef } from "react";

import { useRoofingEstimator, useCart, useSupplier } from "../../../../hooks/query";
import { useProject } from "../../../../hooks";
import { calculateQuantity } from "./utils/productHelpers";
import { FORMULA_EVAL_ORDER } from "../../../../constants/formulaEvalOrder";
import {
    applyRidgeVentMeasurementParams,
    getAttributeQty,
    getAttributeValue,
    getMeasurementLf,
    getRidgeVentLf,
    getBoxVentCount,
    getTurbineVentCount,
    isRoofVentilationType,
    RIDGE_VENT_VENTILATION_TYPE,
    sanitizeVariableName,
} from "../../../../utils/formulaScope";
import type { LegacyDependencyRule } from "../../../../types/productDependencies";
import { applyInspectionMaterialPrefills } from "./utils/inspectionMaterialPrefill";
import {
    flattenMaterialSelectionsForSync,
    hydrateMultiLineFromCart,
    isLaborCartItem,
    isMultiLineCategory,
    isMultiLineSelection,
    isVisibleMaterialCategory,
    materialQuantitiesDiffer,
    normalizeMaterialQuantity,
} from "./utils/materialLineHelpers";

const FIELD_DRY_IN_CATEGORIES = ["Field Dry In Synthetic", "Field Dry In 15 Felt"];
const VALLEY_METAL_CATEGORY = "Valley metal";
const ROLL_VALLEY_CATEGORY = "Roll Valley";
const RIDGE_VENT_CATEGORY = "Ridge Vent";
const BOX_ROOF_VENT_CATEGORY = "Box Roof Vent";
const TURBINE_ROOF_VENT_CATEGORY = "Turbine Roof Vent";
const OPEN_W_VALLEY_TYPES = new Set(["Open (W) Valley", "Open Metal"]);
const CLOSED_VALLEY_TYPES = new Set(["Closed Valley"]);
const FURNACE_CATEGORIES = ["Furnace Jacks", "Furnace Cap", "Furnace Collar"];

function getFurnaceVentCount(attributes: any[] | undefined): number {
    const legacyQty = getAttributeQty(attributes, "Furnace Vents");
    if (legacyQty > 0) return legacyQty;

    const raw = getAttributeValue(attributes, "Furnace Vent Size");
    if (!raw) return 0;

    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return parsed.reduce((sum: number, entry: any) => sum + (Number(entry?.count) || 0), 0);
        }
    } catch {
        // not JSON
    }
    return 0;
}

function getValleyLf(measurements: any, attributes: any[] | undefined): number {
    return getMeasurementLf(measurements, attributes, "Valley");
}

function getRollValleyLf(measurements: any, attributes: any[] | undefined): number {
    return getMeasurementLf(measurements, attributes, ROLL_VALLEY_CATEGORY);
}

function shouldShowValleyMetal(measurements: any, attributes: any[] | undefined): boolean {
    const valleyType = getAttributeValue(attributes, "Valley Type");
    if (!valleyType || !OPEN_W_VALLEY_TYPES.has(valleyType)) return false;
    return getValleyLf(measurements, attributes) > 0;
}

function shouldShowRollValley(measurements: any, attributes: any[] | undefined): boolean {
    const valleyType = getAttributeValue(attributes, "Valley Type");
    if (!valleyType || !CLOSED_VALLEY_TYPES.has(valleyType)) return false;
    return getRollValleyLf(measurements, attributes) > 0 || getValleyLf(measurements, attributes) > 0;
}

function applyValleyTypeCategories(schema: any[], measurements: any, attributes: any[] | undefined): any[] {
    const byName = new Map<string, any>(
        schema.map((item) => {
            const name = item.Name || item.ProductName;
            return [name, item];
        }),
    );

    if (shouldShowValleyMetal(measurements, attributes)) {
        if (!byName.has(VALLEY_METAL_CATEGORY)) {
            byName.set(VALLEY_METAL_CATEGORY, {
                Name: VALLEY_METAL_CATEGORY,
                ProductName: VALLEY_METAL_CATEGORY,
                MeasurementType: [{ Type: "Valley", Unit: "LF" }],
                Subcategories: [],
            });
        }
    } else {
        byName.delete(VALLEY_METAL_CATEGORY);
    }

    if (shouldShowRollValley(measurements, attributes)) {
        if (!byName.has(ROLL_VALLEY_CATEGORY)) {
            byName.set(ROLL_VALLEY_CATEGORY, {
                Name: ROLL_VALLEY_CATEGORY,
                ProductName: ROLL_VALLEY_CATEGORY,
                MeasurementType: [{ Type: "Roll Valley", Unit: "LF" }],
                Subcategories: [],
            });
        }
    } else {
        byName.delete(ROLL_VALLEY_CATEGORY);
    }

    return Array.from(byName.values());
}

function shouldShowRidgeVent(measurements: any, attributes: any[] | undefined): boolean {
    const ventType = getAttributeValue(attributes, "Ventilation Type");
    if (ventType !== RIDGE_VENT_VENTILATION_TYPE) return false;
    return getRidgeVentLf(measurements, attributes) > 0;
}

function shouldShowBoxRoofVent(measurements: any, attributes: any[] | undefined): boolean {
    const ventType = getAttributeValue(attributes, "Ventilation Type");
    if (ventType === RIDGE_VENT_VENTILATION_TYPE) return false;
    if (getBoxVentCount(attributes) > 0) return true;
    return (
        isRoofVentilationType(ventType) &&
        getMeasurementLf(measurements, attributes, BOX_ROOF_VENT_CATEGORY) > 0
    );
}

function shouldShowTurbineRoofVent(measurements: any, attributes: any[] | undefined): boolean {
    const ventType = getAttributeValue(attributes, "Ventilation Type");
    if (ventType === RIDGE_VENT_VENTILATION_TYPE) return false;
    return getTurbineVentCount(attributes) > 0;
}

function setVentCategory(
    byName: Map<string, any>,
    category: string,
    measurementType: string,
    show: boolean,
): void {
    if (show) {
        if (!byName.has(category)) {
            byName.set(category, {
                Name: category,
                ProductName: category,
                MeasurementType: [{ Type: measurementType, Unit: "Count" }],
                Subcategories: [],
            });
        }
    } else {
        byName.delete(category);
    }
}

function applyVentilationTypeCategories(schema: any[], measurements: any, attributes: any[] | undefined): any[] {
    const byName = new Map<string, any>(
        schema.map((item) => {
            const name = item.Name || item.ProductName;
            return [name, item];
        }),
    );

    if (shouldShowRidgeVent(measurements, attributes)) {
        if (!byName.has(RIDGE_VENT_CATEGORY)) {
            byName.set(RIDGE_VENT_CATEGORY, {
                Name: RIDGE_VENT_CATEGORY,
                ProductName: RIDGE_VENT_CATEGORY,
                MeasurementType: [{ Type: "Ridge", Unit: "LF" }],
                Subcategories: [],
            });
        }
    } else {
        byName.delete(RIDGE_VENT_CATEGORY);
    }

    setVentCategory(byName, BOX_ROOF_VENT_CATEGORY, "BoxVents", shouldShowBoxRoofVent(measurements, attributes));
    setVentCategory(
        byName,
        TURBINE_ROOF_VENT_CATEGORY,
        "TurbineVents",
        shouldShowTurbineRoofVent(measurements, attributes),
    );
    byName.delete("Roof Vents");

    return Array.from(byName.values());
}

function applyFurnaceCategories(schema: any[], attributes: any[] | undefined): any[] {
    const byName = new Map<string, any>(
        schema.map((item) => {
            const name = item.Name || item.ProductName;
            return [name, item];
        }),
    );

    const show = getFurnaceVentCount(attributes) >= 1;

    for (const categoryName of FURNACE_CATEGORIES) {
        if (show) {
            if (!byName.has(categoryName)) {
                byName.set(categoryName, {
                    Name: categoryName,
                    ProductName: categoryName,
                    MeasurementType: [{ Type: "FurnaceVents", Unit: "Count" }],
                    Subcategories: [],
                });
            }
        } else {
            byName.delete(categoryName);
        }
    }

    return Array.from(byName.values());
}

function ensureFieldDryInCategories(schema: any[], dependencies: LegacyDependencyRule[]): any[] {
    const byName = new Map<string, any>(
        schema.map((item) => {
            const name = item.Name || item.ProductName;
            return [name, item];
        }),
    );

    for (const categoryName of FIELD_DRY_IN_CATEGORIES) {
        if (byName.has(categoryName)) continue;

        const rule = dependencies.find((dep) => dep.Category === categoryName);
        byName.set(categoryName, {
            Name: categoryName,
            ProductName: categoryName,
            MeasurementType: [{ Type: "Area", Unit: "SQ" }],
            Subcategories: [],
            PrerequisitesOn: rule?.PrerequisiteCategories ?? [],
        });
    }

    return Array.from(byName.values());
}

interface UseMaterialsMasterDataInitProps {
    measurements: any;
    formulas?: any;
    attributes?: any[];
    attributesReady?: boolean;
    dependencies: LegacyDependencyRule[];
    // States
    materialSchema: any[];
    // Setters
    setMaterialSchema: (schema: any[]) => void;
    setCart: (cart: any[]) => void;
    setPriceRanges: (ranges: Record<string, { min: number; max: number }>) => void;
    setIdentifiedProducts: (products: any) => void;
    setMissingProducts: (products: any[]) => void;
    setIsLoading: (isLoading: boolean) => void;
    applyHydratedSelections: (values: Record<string, any>) => void;
}

/**
 * Initialization Hook: Loads data from API and hydrates the local UI state.
 */
export default function useMaterialsMasterDataInit(stateSetters: UseMaterialsMasterDataInitProps) {
    const {
        measurements,
        formulas,
        attributes,
        attributesReady,
        dependencies,
        setMaterialSchema,
        setCart,
        setPriceRanges,
        setIdentifiedProducts,
        setMissingProducts,
        setIsLoading,
        applyHydratedSelections,
    } = stateSetters;

    const { project } = useProject();
    const { useIdentifyProducts } = useRoofingEstimator();
    const { useListByProjectId, useDelete } = useCart();
    const { useListPriceRanges } = useSupplier();

    const identifiedProductsSvc = useIdentifyProducts();
    const cartListSvc = useListByProjectId();
    const deleteCartSvc = useDelete();
    const listPriceRangesSvc = useListPriceRanges();
    const loadRequestRef = useRef(0);
    const deleteCartSvcRef = useRef(deleteCartSvc);
    deleteCartSvcRef.current = deleteCartSvc;
    const applyHydratedSelectionsRef = useRef(applyHydratedSelections);
    applyHydratedSelectionsRef.current = applyHydratedSelections;

    const fetchCartItems = useCallback(async () => {
        if (!project?.id) return [];
        return await cartListSvc.mutateAsync({ ProjectId: project.id });
    }, [project?.id, cartListSvc]);

    const getRequiredMaterials = async () => {
        try {
            const params: Record<string, number> = {};

            if (Array.isArray(measurements)) {
                measurements.forEach((m) => {
                    const raw = m.ActualValue ?? m.CalculatedValue;
                    const num = Number(raw);
                    if (m.MeasurementType && !isNaN(num) && num > 0) {
                        params[m.MeasurementType] = num;
                    }
                });
            }

            if (attributes) {
                attributes.forEach((attr) => {
                    const name = attr.AttributeName || attr.Name || attr.key;
                    const qty =
                        attr.Qty !== null && attr.Qty !== undefined
                            ? Number(attr.Qty)
                            : attr.CalculatedValue !== null && attr.CalculatedValue !== undefined
                              ? Number(attr.CalculatedValue)
                              : 0;
                    if (name && qty > 0) {
                        const key = sanitizeVariableName(name);
                        params[key] = qty;
                    }
                });
            }

            applyRidgeVentMeasurementParams(params, attributes);

            const furnaceCount = getFurnaceVentCount(attributes);
            if (furnaceCount >= 1) {
                params.FurnaceVents = furnaceCount;
                params["Furnace Vents"] = furnaceCount;
            }

            return await identifiedProductsSvc.mutateAsync(params);
        } catch (error) {
            console.error("Error in getRequiredMaterials:", error);
            return [];
        }
    };

    const getSchemaWithDependencies = (requiredMaterials: any[], allAvailableMaterials: any[]) => {
        const allAvailableWithMeta = allAvailableMaterials.map((item: any) => ({
            ...item,
            name: item.ProductName ?? item.Name,
            Name: item.ProductName ?? item.Name,
        }));

        const requiredMaterialsWithMeta = requiredMaterials
            .map(({ ProductName }: any) => {
                const matched = allAvailableWithMeta.find((item: any) => item.name === ProductName);
                return matched || null;
            })
            .filter(Boolean);

        const productSelectionMetaData = requiredMaterialsWithMeta.reduce((acc: any, curr: any) => {
            const name = curr.name;

            if (!acc[name]) {
                acc[name] = { ...curr };
            }

            const dependency = dependencies.find((depProd) => depProd.Category === name);
            if (dependency?.PrerequisiteCategories?.length) {
                acc[name] = {
                    ...acc[name],
                    PrerequisitesOn: [
                        ...(acc[name].PrerequisitesOn || []),
                        ...dependency.PrerequisiteCategories.filter(
                            (category: string) => !(acc[name].PrerequisitesOn || []).includes(category),
                        ),
                    ],
                };
            }

            return acc;
        }, {});

        return Object.values(productSelectionMetaData);
    };

    const calculatePriceRanges = useCallback(
        async (schema: any[]) => {
            if (schema.length === 0) return { ranges: {}, raw: [] };

            try {
                const apiRanges = await listPriceRangesSvc.mutateAsync();
                const ranges: Record<string, { min: number; max: number }> = {};

                if (Array.isArray(apiRanges)) {
                    apiRanges.forEach((item: any) => {
                        const min = Number(item.MinPrice);
                        const max = Number(item.MaxPrice);
                        const productId = item.ManufacturerProductId;
                        if (productId) {
                            ranges[productId] = { min, max };
                        }
                        if (item.ProductName) {
                            ranges[item.ProductName] = { min, max };
                        }

                        const category = item.CategoryName;
                        if (!ranges[category]) {
                            ranges[category] = { min, max };
                        } else {
                            ranges[category].min = Math.min(ranges[category].min, min);
                            ranges[category].max = Math.max(ranges[category].max, max);
                        }
                    });
                }

                setPriceRanges(ranges);
                return { ranges, raw: apiRanges };
            } catch (error) {
                console.error("Error calculating price ranges:", error);
                return { ranges: {}, raw: [] };
            }
        },
        [listPriceRangesSvc, setPriceRanges],
    );

    const loadMasterData = useCallback(async () => {
        if (!project?.id) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        const requestId = ++loadRequestRef.current;
        try {
            const requiredMaterials = await getRequiredMaterials();
            if (!requiredMaterials || !Array.isArray(requiredMaterials)) {
                setIsLoading(false);
                return;
            }

            const [cartItems, { raw: apiRanges }] = await Promise.all([
                fetchCartItems(),
                calculatePriceRanges(requiredMaterials),
            ]);

            const initialSchema = applyFurnaceCategories(
                applyVentilationTypeCategories(
                    applyValleyTypeCategories(
                        ensureFieldDryInCategories(
                            getSchemaWithDependencies(requiredMaterials, requiredMaterials),
                            dependencies,
                        ),
                        measurements,
                        attributes,
                    ),
                    measurements,
                    attributes,
                ),
                attributes,
            );

            const mergeSubcategories = (catalogSubs: any[], categoryName: string) => {
                const targetName = categoryName.toLowerCase().trim();
                const supplierSubs = apiRanges
                    .filter((p: any) => (p.CategoryName || "").toLowerCase().trim() === targetName)
                    .map((p: any) => ({
                        Id: p.ManufacturerProductId,
                        Name: p.ProductName,
                        ProductName: p.ProductName,
                        SystemLine: p.SystemLine,
                    }));

                const byKey = new Map<string, any>();
                for (const sub of [...catalogSubs, ...supplierSubs]) {
                    const key = (sub.Id || sub.Name || "").toString().toLowerCase();
                    if (!key) continue;
                    const supplierMatch = apiRanges.find(
                        (p: any) => p.ManufacturerProductId && p.ManufacturerProductId === sub.Id,
                    );
                    const existing = byKey.get(key);
                    byKey.set(key, {
                        ...existing,
                        ...sub,
                        Id: sub.Id || existing?.Id,
                        Name: sub.Name || sub.ProductName,
                        ProductName: sub.ProductName || sub.Name,
                        SystemLine: sub.SystemLine || supplierMatch?.SystemLine || existing?.SystemLine,
                        Color: sub.Color ?? existing?.Color,
                    });
                }
                return Array.from(byKey.values());
            };

            const finalMaterialSchema = initialSchema
                .filter((category: any) => isVisibleMaterialCategory(category.Name || category.ProductName))
                .map((category: any) => {
                const catalogSubs = category.Subcategories || [];
                const merged = mergeSubcategories(catalogSubs, category.Name || "");
                return { ...category, Subcategories: merged };
            });

            // Hydration Logic — materials cart rows only (Labor is synced separately)
            const hydratedSelections: Record<string, any> = {};
            cartItems.forEach((item: any) => {
                if (!item.ProductCategory || isLaborCartItem(item)) return;

                if (isMultiLineCategory(item.ProductCategory)) {
                    hydrateMultiLineFromCart(hydratedSelections, item);
                    return;
                }

                hydratedSelections[item.ProductCategory] = {
                    id: item.Id,
                    value: item.ProductName,
                    quantity: normalizeMaterialQuantity(item.ItemCount),
                    color: item.Color,
                    manufacturerProductId: item.ManufacturerProductId,
                    systemLine: item.SystemLine,
                    supplierId: item.SupplierId,
                    supplierName: item.SupplierName,
                    unitPrice: item.UnitPrice,
                };
            });

            const visibleCategories = new Set(finalMaterialSchema.map((item: any) => item.Name || item.ProductName));
            Object.keys(hydratedSelections).forEach((category) => {
                if (!visibleCategories.has(category)) {
                    delete hydratedSelections[category];
                }
            });

            applyInspectionMaterialPrefills(
                hydratedSelections,
                attributes,
                finalMaterialSchema,
                measurements,
                formulas,
            );

            // Enforce formula-based quantities — Field Shingles first so dependents get FieldShingles in scope
            if (formulas) {
                const hydrationOrder = [
                    ...FORMULA_EVAL_ORDER.filter((cat) => cat in hydratedSelections),
                    ...Object.keys(hydratedSelections).filter((cat) => !FORMULA_EVAL_ORDER.includes(cat)),
                ];
                hydrationOrder.forEach((category) => {
                    const sel = hydratedSelections[category];
                    if (isMultiLineSelection(sel)) return;
                    if (sel && sel.value) {
                        const correctQty = calculateQuantity(
                            finalMaterialSchema,
                            measurements,
                            category,
                            sel.value,
                            formulas,
                            attributes,
                            hydratedSelections,
                        );
                        const normalizedQty = normalizeMaterialQuantity(correctQty);
                        if (materialQuantitiesDiffer(normalizedQty, sel.quantity)) {
                            sel.quantity = normalizedQty;
                        }
                        if (normalizedQty <= 0) {
                            delete hydratedSelections[category];
                        }
                    }
                });
            }

            if (requestId !== loadRequestRef.current) return;

            const finalFlat = flattenMaterialSelectionsForSync(hydratedSelections);
            const retainedIds = new Set(
                Object.values(finalFlat)
                    .map((item: any) => item.id)
                    .filter(Boolean),
            );
            const orphanIds = cartItems
                .filter((item: any) => item.Id && !isLaborCartItem(item) && !retainedIds.has(item.Id))
                .map((item: any) => item.Id as string);

            for (const id of orphanIds) {
                try {
                    await deleteCartSvcRef.current.mutateAsync({ Id: id });
                } catch (err: any) {
                    if (err?.response?.status !== 404) {
                        console.error(`[MaterialsInit] Failed to delete orphan cart item ${id}:`, err);
                    }
                }
            }

            applyHydratedSelectionsRef.current(hydratedSelections);

            setIdentifiedProducts(requiredMaterials);
            setCart(cartItems);
            setMaterialSchema(finalMaterialSchema);

            const missing = finalMaterialSchema
                .filter((item: any) => {
                    const optionCount = item.Subcategories?.length ?? 0;
                    return optionCount === 0;
                })
                .map((item: any) => item.Name || item.ProductName);

            setMissingProducts(missing);
        } catch (error) {
            console.error("Error loading materials data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [
        project?.id,
        measurements,
        formulas,
        attributes,
        dependencies,
        setIsLoading,
        setMissingProducts,
        fetchCartItems,
        calculatePriceRanges,
        setIdentifiedProducts,
        setCart,
        setMaterialSchema,
    ]);

    useEffect(() => {
        if (!attributesReady || !formulas || !Array.isArray(measurements) || measurements.length === 0) return;

        const timer = window.setTimeout(() => {
            void loadMasterData();
        }, 150);

        return () => window.clearTimeout(timer);
        // loadMasterData is intentionally omitted — it is recreated when inputs change anyway.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [measurements, formulas, attributes, dependencies, attributesReady]);

    return {
        loadMasterData,
        fetchCartItems,
        calculatePriceRanges,
    };
}
